import React, { useState, useEffect } from 'react';
import { getVehicles, getPositions, getDrivers, getTripReport, getGeofences } from '../utils/api';

// ── Fleet Page ─────────────────────────────────────────────────
export function FleetPage() {
  const [vehicles, setVehicles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('');
  const [sortKey, setSortKey] = useState('displayName');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    Promise.all([getVehicles(), getPositions()])
      .then(([v, p]) => { setVehicles(v.vehicles || []); setPositions(p.positions || []); })
      .finally(() => setLoading(false));
  }, []);

  // positions.vehiIdno is the device ID from CMSV6 getDeviceStatus — matches vehicle.devId
  const getOnline = (devId) => {
    if (!devId) return null;
    const p = positions.find(p => p.vehiIdno === devId);
    return p ? p.online : null;
  };

  const companies = [...new Set(vehicles.map(v => v.companyName).filter(Boolean))].sort();
  const filtered  = company ? vehicles.filter(v => v.companyName === company) : vehicles;

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey] ?? '';
    const bv = b[sortKey] ?? '';
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const onlineCount  = vehicles.filter(v => getOnline(v.devId) === true).length;
  const offlineCount = vehicles.filter(v => getOnline(v.devId) === false).length;

  const SortArrow = ({ col }) => {
    if (sortKey !== col) return <span style={{ color:'#ddd', marginLeft:4, fontSize:10 }}>↕</span>;
    return <span style={{ color:'#185FA5', marginLeft:4, fontSize:10 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const Th = ({ col, label, mono }) => (
    <th onClick={() => handleSort(col)}
      style={{ textAlign:'left', padding:'9px 12px', fontSize:11, color:'#888', borderBottom:'1px solid #eee',
               fontWeight:500, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
               fontFamily: mono ? 'monospace' : 'inherit' }}>
      {label}<SortArrow col={col} />
    </th>
  );

  const fmtDate = (s) => s ? s.substring(0, 10) : '—';

  return (
    <div>
      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:14 }}>
        {[
          ['Total vehicles', vehicles.length, '#E6F1FB', '#0C447C'],
          ['Online',         onlineCount,     '#EAF3DE', '#3B6D11'],
          ['Offline',        offlineCount,    '#FCEBEB', '#A32D2D'],
          ['Companies',      companies.length,'#FAEEDA', '#854F0B'],
        ].map(([l, v, bg, c]) => (
          <div key={l} style={{ background:bg, borderRadius:8, padding:'12px 14px' }}>
            <div style={{ fontSize:11, color:c, opacity:.7 }}>{l}</div>
            <div style={{ fontSize:22, fontWeight:500, color:c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Company filter pills */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', marginBottom:12 }}>
        <span style={{ fontSize:11, color:'#aaa', marginRight:2 }}>Company:</span>
        {[{ label:'All', value:'' }, ...companies.map(c => ({ label:c, value:c }))].map(({ label, value }) => {
          const count  = value ? vehicles.filter(v => v.companyName === value).length : vehicles.length;
          const active = company === value;
          return (
            <button key={value || '__all'} onClick={() => setCompany(value)}
              style={{ padding:'4px 11px', borderRadius:20, fontSize:11, border:'0.5px solid',
                       borderColor: active ? '#185FA5' : '#ddd',
                       background:  active ? '#185FA5' : 'white',
                       color:       active ? 'white'   : '#555',
                       cursor:'pointer', fontWeight: active ? 500 : 400, lineHeight:1.6 }}>
              {label}&nbsp;<span style={{ opacity:.65 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div style={{ background:'white', border:'1px solid #e8e8e8', borderRadius:12, overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:24, fontSize:12, color:'#aaa' }}>Loading vehicles from CMSV6...</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'#fafafa' }}>
                <Th col="displayName"  label="Plate number" />
                <Th col="companyName"  label="Company" />
                <Th col="devId"        label="Device ID"    mono />
                <Th col="simNumber"    label="SIM"          mono />
                <Th col="channelCount" label="Channels" />
                <Th col="installDate"  label="Install date" />
                <th style={{ textAlign:'left', padding:'9px 12px', fontSize:11, color:'#888',
                             borderBottom:'1px solid #eee', fontWeight:500 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={7} style={{ padding:'24px', textAlign:'center', color:'#aaa' }}>
                  No vehicles found
                </td></tr>
              )}
              {sorted.map((v, i) => {
                const online = getOnline(v.devId);
                return (
                  <tr key={i}
                    style={{ borderBottom:'1px solid #f5f5f5', transition:'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f7faff'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding:'9px 12px', fontWeight:600 }}>{v.displayName || '—'}</td>
                    <td style={{ padding:'9px 12px', color:'#555' }}>{v.companyName || '—'}</td>
                    <td style={{ padding:'9px 12px', color:'#888', fontFamily:'monospace', fontSize:11 }}>{v.devId || '—'}</td>
                    <td style={{ padding:'9px 12px', color:'#888', fontFamily:'monospace', fontSize:11 }}>{v.simNumber || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {v.channelCount > 0 ? (
                        <span title={v.channelNames.join(', ')}
                          style={{ background:'#E6F1FB', color:'#0C447C', fontSize:10,
                                   padding:'2px 8px', borderRadius:8, fontWeight:500, cursor:'help' }}>
                          {v.channelCount} CH
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding:'9px 12px', color:'#888' }}>{fmtDate(v.installDate)}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {online === null
                        ? <span style={{ color:'#bbb', fontSize:10 }}>—</span>
                        : <span style={{
                            background: online ? '#EAF3DE' : '#FCEBEB',
                            color:      online ? '#3B6D11' : '#A32D2D',
                            fontSize:10, padding:'2px 8px', borderRadius:8, fontWeight:500 }}>
                            {online ? 'Online' : 'Offline'}
                          </span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Drivers Page ───────────────────────────────────────────────
export function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDrivers().then(d => setDrivers(d.drivers || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'10px 14px', borderBottom:'0.5px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:12, fontWeight:500, color:'#666' }}>Drivers ({drivers.length})</span>
        </div>
        {loading ? <div style={{ padding:20, fontSize:12, color:'#aaa' }}>Loading drivers...</div> : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'#fafafa' }}>
                {['Name','RFID','Phone','License','Vehicle','Status'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:'#999', borderBottom:'0.5px solid #eee', fontWeight:500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drivers.length === 0 && <tr><td colSpan={6} style={{ padding:'20px', textAlign:'center', color:'#aaa' }}>No drivers found in CMSV6</td></tr>}
              {drivers.map((d, i) => (
                <tr key={i} style={{ borderBottom:'0.5px solid #f5f5f5' }}>
                  <td style={{ padding:'9px 12px', fontWeight:500 }}>{d.name || d.driverName || '—'}</td>
                  <td style={{ padding:'9px 12px', color:'#888', fontFamily:'monospace' }}>{d.rfid || d.idCard || '—'}</td>
                  <td style={{ padding:'9px 12px', color:'#888' }}>{d.phone || d.tel || '—'}</td>
                  <td style={{ padding:'9px 12px', color:'#888' }}>{d.licenseNo || '—'}</td>
                  <td style={{ padding:'9px 12px' }}>{d.vehiIdno || d.vehicleNo || '—'}</td>
                  <td style={{ padding:'9px 12px' }}>
                    <span style={{ background:'#EAF3DE', color:'#3B6D11', fontSize:10, padding:'2px 7px', borderRadius:8 }}>Active</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Reports Page ───────────────────────────────────────────────
export function ReportsPage() {
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehiIdno, setVehiIdno] = useState('');
  const [loading, setLoading] = useState(false);
  const today = new Date();
  const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate()-7);
  const fmt = d => d.toISOString().replace('T',' ').substring(0,16);
  const [startTime, setStartTime] = useState(fmt(weekAgo));
  const [endTime, setEndTime] = useState(fmt(today));

  useEffect(() => { getVehicles().then(d => setVehicles(d.vehicles || [])); }, []);

  const search = async () => {
    setLoading(true);
    const s = startTime.replace('T',' ')+':00', e = endTime.replace('T',' ')+':00';
    try { const d = await getTripReport(vehiIdno, s, e); setTrips(d.trips || []); }
    catch { setTrips([]); }
    setLoading(false);
  };

  const totalKm = trips.reduce((a,t) => a + (t.mileage||0), 0);
  const totalDur = trips.reduce((a,t) => a + (t.duration||0), 0);

  return (
    <div>
      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:10, padding:'12px 16px', marginBottom:12, display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div>
          <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>Vehicle</div>
          <select value={vehiIdno} onChange={e => setVehiIdno(e.target.value)} style={{ fontSize:12, padding:'5px 8px', border:'0.5px solid #ddd', borderRadius:6, minWidth:130 }}>
            <option value="">All vehicles</option>
            {vehicles.map((v,i) => <option key={i} value={v.devId||v.vehiIdno||v.id}>{v.displayName||`Vehicle ${i+1}`}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>From</div>
          <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ fontSize:12, padding:'5px 8px', border:'0.5px solid #ddd', borderRadius:6 }} />
        </div>
        <div>
          <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>To</div>
          <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ fontSize:12, padding:'5px 8px', border:'0.5px solid #ddd', borderRadius:6 }} />
        </div>
        <button onClick={search} style={{ padding:'6px 16px', background:'#185FA5', color:'white', border:'none', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:500 }}>Generate</button>
      </div>

      {trips.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:12 }}>
          {[['Total trips', trips.length], [`Total distance`, `${totalKm.toFixed(1)} km`], [`Total duration`, `${Math.round(totalDur/60)} min`]].map(([l,v]) => (
            <div key={l} style={{ background:'#f7f8fa', borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontSize:11, color:'#888' }}>{l}</div>
              <div style={{ fontSize:20, fontWeight:500 }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, overflow:'hidden' }}>
        {loading ? <div style={{ padding:20, fontSize:12, color:'#aaa' }}>Loading trip data...</div> : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'#fafafa' }}>
                {['Vehicle','Start','End','Distance','Duration','Max speed','Avg speed'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:'#999', borderBottom:'0.5px solid #eee', fontWeight:500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trips.length === 0 && <tr><td colSpan={7} style={{ padding:'20px', textAlign:'center', color:'#aaa' }}>No trips found. Generate a report above.</td></tr>}
              {trips.map((t, i) => (
                <tr key={i} style={{ borderBottom:'0.5px solid #f5f5f5' }}>
                  <td style={{ padding:'8px 12px', fontWeight:500 }}>{t.vehiIdno||'—'}</td>
                  <td style={{ padding:'8px 12px', color:'#888' }}>{t.startTime||'—'}</td>
                  <td style={{ padding:'8px 12px', color:'#888' }}>{t.endTime||'—'}</td>
                  <td style={{ padding:'8px 12px' }}>{t.mileage ? `${t.mileage.toFixed(1)} km` : '—'}</td>
                  <td style={{ padding:'8px 12px' }}>{t.duration ? `${Math.round(t.duration/60)} min` : '—'}</td>
                  <td style={{ padding:'8px 12px', color:'#E24B4A' }}>{t.maxSpeed ? `${Math.round(t.maxSpeed)} km/h` : '—'}</td>
                  <td style={{ padding:'8px 12px' }}>{t.avgSpeed ? `${Math.round(t.avgSpeed)} km/h` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Geofence Page ──────────────────────────────────────────────
export function GeofencePage() {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGeofences().then(d => setAreas(d.areas || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, overflow:'hidden' }}>
        <div style={{ padding:'10px 14px', borderBottom:'0.5px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:12, fontWeight:500, color:'#666' }}>Geofence areas ({areas.length})</span>
          <span style={{ fontSize:11, color:'#aaa' }}>Manage areas in CMSV6 client</span>
        </div>
        {loading ? <div style={{ padding:20, fontSize:12, color:'#aaa' }}>Loading geofences...</div> : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'#fafafa' }}>
                {['Name','Type','Vehicles','Alert on'].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'8px 12px', fontSize:11, color:'#999', borderBottom:'0.5px solid #eee', fontWeight:500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {areas.length === 0 && <tr><td colSpan={4} style={{ padding:'20px', textAlign:'center', color:'#aaa' }}>No geofences defined in CMSV6</td></tr>}
              {areas.map((a, i) => (
                <tr key={i} style={{ borderBottom:'0.5px solid #f5f5f5' }}>
                  <td style={{ padding:'9px 12px', fontWeight:500 }}>{a.name||`Area ${i+1}`}</td>
                  <td style={{ padding:'9px 12px' }}><span style={{ background:'#E6F1FB', color:'#0C447C', fontSize:10, padding:'2px 7px', borderRadius:8 }}>{a.type||'Polygon'}</span></td>
                  <td style={{ padding:'9px 12px', color:'#888' }}>{a.vehicleCount||'All'}</td>
                  <td style={{ padding:'9px 12px', color:'#888' }}>{a.alertType||'Enter & Exit'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Settings Page ──────────────────────────────────────────────
export function SettingsPage() {
  return (
    <div style={{ maxWidth:520 }}>
      {[
        { title:'CMSV6 connection', items:[['Server IP', '103.6.245.78'], ['Port', '6605'], ['Protocol', 'HTTP'], ['Status', 'Connected']] },
        { title:'Notification settings', items:[['Email alerts', 'Enabled'], ['SMS alerts', 'Disabled'], ['Push notifications', 'Enabled'], ['Alert sound', 'On']] },
        { title:'System', items:[['Timezone', 'Asia/Kuala_Lumpur'], ['Language', 'English'], ['Date format', 'YYYY-MM-DD'], ['Speed unit', 'km/h']] },
      ].map(section => (
        <div key={section.title} style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, overflow:'hidden', marginBottom:12 }}>
          <div style={{ padding:'10px 16px', borderBottom:'0.5px solid #eee', fontSize:12, fontWeight:500, color:'#666' }}>{section.title}</div>
          {section.items.map(([k,v]) => (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'10px 16px', borderBottom:'0.5px solid #f5f5f5', fontSize:12 }}>
              <span style={{ color:'#666' }}>{k}</span>
              <span style={{ fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
      ))}
      <div style={{ background:'#FAEEDA', border:'0.5px solid #EF9F27', borderRadius:8, padding:'10px 14px', fontSize:12, color:'#854F0B' }}>
        To change server settings, update the <code style={{ background:'rgba(0,0,0,.06)', padding:'1px 4px', borderRadius:3 }}>.env</code> file in your proxy server folder and restart.
      </div>
    </div>
  );
}

export default FleetPage;
